/**
 * OperationalWidgets — 7 widgets opérationnels remplaçant les widgets peu utiles
 *  - ConversionRateWidget   : Taux de conversion devis (sent → signed)
 *  - FillRateWidget         : Taux de remplissage sessions
 *  - SessionsToSignWidget   : Sessions à émarger
 *  - ConventionsToSignWidget : Conventions / devis en attente de signature
 *  - UnreadEmailsWidget     : Emails non lus avec lien direct
 *  - TodaySessionsWidget    : Sessions du jour
 *  - NewLearnersMonthWidget : Nouveaux apprenants ce mois
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, AlertCircle, Clock, Users, Mail,
  FileSignature, ClipboardCheck, CalendarClock, UserPlus, Target,
  ChevronRight, MapPin, Video,
} from 'lucide-react';
import api from '../api/client';

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 });

const fmtDate = (iso, format = 'short') => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  if (format === 'time') return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

// ============================================================================
// 1. Taux de conversion devis
// ============================================================================
export function ConversionRateWidget({ period = '30d' }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/dashboard/widget/conversion-rate', { params: { period } })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [period]);
  const up = data?.trend !== null && data?.trend !== undefined && data.trend >= 0;
  return (
    <div data-testid="widget-conversion-rate" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-2 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <Target className="w-3.5 h-3.5 text-indigo-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Taux de conversion</h3>
        </div>
        {data?.trend !== null && data?.trend !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {up ? '+' : ''}{data.trend}pts
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center">
        <div className="text-[34px] font-bold text-indigo-600 leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {data ? `${data.rate}%` : '—'}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          {data ? <>{data.signed_count} signés / {data.sent_count} envoyés</> : 'Chargement…'}
        </div>
        <div className="text-[10px] text-slate-400 mt-1">sur {period === '7d' ? '7 jours' : period === '30d' ? '30 jours' : period === '90d' ? '90 jours' : '1 an'}</div>
      </div>
    </div>
  );
}

// ============================================================================
// 2. Taux de remplissage
// ============================================================================
export function FillRateWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/dashboard/widget/fill-rate').then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  const color = (data?.rate || 0) >= 75 ? '#059669' : (data?.rate || 0) >= 50 ? '#F59E0B' : '#DC2626';
  return (
    <div data-testid="widget-fill-rate" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-2 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <Users className="w-3.5 h-3.5 text-amber-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Remplissage · 30j</h3>
        </div>
      </div>
      {data && (
        <>
          <div className="flex items-center justify-center mb-1 flex-shrink-0">
            <div className="text-[28px] font-bold leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", color }}>
              {data.rate}%
            </div>
          </div>
          <div className="text-[10.5px] text-slate-500 text-center mb-2">{data.learners_total} / {data.capacity_total} places</div>
          {data.underfilled && data.underfilled.length > 0 && (
            <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
              <div className="text-[10px] text-amber-700 font-bold uppercase mb-1">À remplir 🔥</div>
              {data.underfilled.slice(0, 3).map((s) => (
                <div key={s.id} className="text-[10.5px] py-1 px-1.5 rounded hover:bg-slate-50">
                  <div className="font-semibold text-slate-900 truncate">{s.title}</div>
                  <div className="text-slate-500">{fmtDate(s.start_date)} · {s.learners}/{s.capacity} ({s.fill_pct}%)</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================================
// 3. Sessions à émarger
// ============================================================================
export function SessionsToSignWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0 });
  useEffect(() => {
    api.get('/dashboard/widget/sessions-to-sign').then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="widget-sessions-to-sign" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <ClipboardCheck className="w-3.5 h-3.5 text-orange-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Sessions à émarger</h3>
        </div>
        {data.total_count > 0 && (
          <span className="text-[10px] font-semibold rounded bg-orange-50 text-orange-700 px-1.5 py-0.5 border border-orange-100">
            {data.total_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Toutes les sessions sont émargées ✨</div>
        )}
        {data.rows.map((s) => (
          <Link
            to={`/app/sessions?id=${encodeURIComponent(s.id)}`}
            key={s.id}
            data-testid={`emarge-row-${s.id}`}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-orange-50 border border-transparent hover:border-orange-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-md bg-orange-100 flex items-center justify-center flex-shrink-0">
              <ClipboardCheck className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-slate-900 truncate">{s.title}</div>
              <div className="text-[10.5px] text-slate-500">
                <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                Terminée il y a {s.days_since}j · {s.learners} apprenants
              </div>
            </div>
            <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// 4. Conventions / Documents à signer
// ============================================================================
export function ConventionsToSignWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0 });
  useEffect(() => {
    api.get('/dashboard/widget/conventions-to-sign').then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="widget-conventions-to-sign" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <FileSignature className="w-3.5 h-3.5 text-violet-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Documents à signer</h3>
        </div>
        {data.total_count > 0 && (
          <span className="text-[10px] font-semibold rounded bg-violet-50 text-violet-700 px-1.5 py-0.5 border border-violet-100">
            {data.total_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Aucun document en attente ✨</div>
        )}
        {data.rows.map((d) => (
          <Link
            to={d.type === 'Devis' ? `/app/devis?id=${encodeURIComponent(d.id)}` : `/app/conventions?id=${encodeURIComponent(d.id)}`}
            key={d.id}
            data-testid={`conv-row-${d.id}`}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-violet-50 border border-transparent hover:border-violet-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-md bg-violet-100 flex items-center justify-center flex-shrink-0">
              <FileSignature className="w-4 h-4 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-[12px] font-semibold text-slate-900 truncate">{d.client}</span>
                <span className="text-[9.5px] text-violet-500 font-mono">{d.id}</span>
              </div>
              <div className="text-[10.5px] text-slate-500">
                {d.type} · {fmtMoney(d.amount)} € · {d.days_pending}j en attente
              </div>
            </div>
            <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// 5. Emails non lus
// ============================================================================
export function UnreadEmailsWidget() {
  const [data, setData] = useState({ latest: [], total_count: 0 });
  useEffect(() => {
    api.get('/dashboard/widget/unread-emails').then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="widget-unread-emails" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <Mail className="w-3.5 h-3.5 text-sky-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Emails non lus</h3>
        </div>
        {data.total_count > 0 && (
          <Link to="/app/email" data-testid="link-to-email" className="text-[10px] font-semibold rounded bg-sky-50 text-sky-700 px-1.5 py-0.5 border border-sky-100 hover:bg-sky-100">
            {data.total_count} · Voir tout
          </Link>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {data.total_count === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Inbox zéro 🎉</div>
        )}
        {data.latest.map((m, i) => (
          <Link
            to={`/app/email?id=${encodeURIComponent(m.id || '')}`}
            key={m.id || i}
            className="flex flex-col rounded-md px-2 py-1.5 hover:bg-sky-50 border border-transparent hover:border-sky-100 transition-colors"
          >
            <div className="text-[11.5px] font-semibold text-slate-900 truncate">{m.from || 'Inconnu'}</div>
            <div className="text-[10.5px] text-slate-500 truncate">{m.subject || '(Sans objet)'}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// 6. Sessions du jour
// ============================================================================
export function TodaySessionsWidget() {
  const [data, setData] = useState({ rows: [], total_count: 0 });
  useEffect(() => {
    api.get('/dashboard/widget/today-sessions').then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="widget-today-sessions" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <CalendarClock className="w-3.5 h-3.5 text-emerald-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Sessions du jour</h3>
        </div>
        {data.total_count > 0 && (
          <span className="text-[10px] font-semibold rounded bg-emerald-50 text-emerald-700 px-1.5 py-0.5 border border-emerald-100">
            {data.total_count}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-1">
        {data.rows.length === 0 && (
          <div className="text-center text-slate-400 text-[11px] italic py-6">Aucune session aujourd'hui — journée libre 🌴</div>
        )}
        {data.rows.map((s) => (
          <Link
            to={`/app/sessions?id=${encodeURIComponent(s.id)}`}
            key={s.id}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-emerald-50 border border-transparent hover:border-emerald-100 transition-colors"
          >
            <div className="w-10 text-center flex-shrink-0">
              <div className="text-[11px] font-bold text-emerald-700">{s.start_time}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-slate-900 truncate">{s.title}</div>
              <div className="text-[10.5px] text-slate-500 flex items-center gap-1.5">
                {s.mode === 'Distanciel' ? <Video className="w-2.5 h-2.5" /> : <MapPin className="w-2.5 h-2.5" />}
                <span className="truncate">{s.location}</span>
                <span>· {s.learners} ap.</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// 7. Nouveaux apprenants ce mois
// ============================================================================
export function NewLearnersMonthWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/dashboard/widget/new-learners-month').then((r) => setData(r.data)).catch(() => {});
  }, []);
  const up = data?.trend !== null && data?.trend !== undefined && data.trend >= 0;
  return (
    <div data-testid="widget-new-learners" className="rounded-xl bg-white border border-[#EFF0F6] p-3 flex flex-col min-h-0 h-full overflow-hidden">
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0 drag-handle">
        <div className="flex items-center gap-1.5 min-w-0">
          <UserPlus className="w-3.5 h-3.5 text-pink-500" />
          <h3 className="text-[11px] font-bold text-slate-900 uppercase tracking-wide truncate">Nouveaux apprenants</h3>
        </div>
        {data?.trend !== null && data?.trend !== undefined && (
          <span className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${up ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
            {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
            {up ? '+' : ''}{data.trend}%
          </span>
        )}
      </div>
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="text-center mb-2 flex-shrink-0">
          <div className="text-[28px] font-bold text-pink-600 leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {data?.count ?? '—'}
          </div>
          <div className="text-[10.5px] text-slate-500 mt-1 capitalize">{data?.month_label || ''}</div>
        </div>
        {data?.latest && data.latest.length > 0 && (
          <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1 space-y-0.5">
            {data.latest.map((lr) => (
              <Link
                to={`/app/learners?id=${encodeURIComponent(lr.id)}`}
                key={lr.id}
                className="block text-[10.5px] py-1 px-1.5 rounded hover:bg-pink-50"
              >
                <div className="font-semibold text-slate-900 truncate">{lr.name}</div>
                <div className="text-slate-500 truncate">{lr.company} · {fmtDate(lr.created_at)}</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
